import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, MenuItem, TextField, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert, Divider, Box,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rfqApi, poApi, type Rfq } from '../../api/procurement.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function RfqDetailDialog({ rfqId, onClose }: { rfqId: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = rfqId !== null;
  const [error, setError] = useState<string | null>(null);
  const [quoteVendor, setQuoteVendor] = useState<number | ''>('');
  const [rates, setRates] = useState<Record<number, number>>({});
  const [toast, setToast] = useState<string | null>(null);

  const rfq = useQuery({ queryKey: ['rfq', rfqId], queryFn: () => rfqApi.get(rfqId as number), enabled: open });
  const comparison = useQuery({ queryKey: ['rfq-cmp', rfqId], queryFn: () => rfqApi.comparison(rfqId as number), enabled: open });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['rfq', rfqId] }); qc.invalidateQueries({ queryKey: ['rfq-cmp', rfqId] }); qc.invalidateQueries({ queryKey: ['rfqs'] }); };

  useEffect(() => { setQuoteVendor(''); setRates({}); setError(null); }, [rfqId]);

  const items = rfq.data?.items ?? [];

  const submitQuote = useMutation({
    mutationFn: () => rfqApi.submitQuotation(rfqId as number, { vendorId: Number(quoteVendor), items: items.map((it) => ({ rfqItemId: it.id, unitRate: rates[it.id] ?? 0, taxPercent: 18 })) }),
    onSuccess: () => { setQuoteVendor(''); setRates({}); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const selectMut = useMutation({ mutationFn: (qid: number) => rfqApi.select(rfqId as number, qid), onSuccess: refresh, onError: (e) => setError(extractApiError(e)) });
  const approveMut = useMutation({ mutationFn: () => rfqApi.approveSelection(rfqId as number), onSuccess: refresh, onError: (e) => setError(extractApiError(e)) });
  const genPo = useMutation({ mutationFn: () => poApi.create({ rfqId: rfqId as number }), onSuccess: (po) => { setToast(`PO ${po.code} generated`); refresh(); }, onError: (e) => setError(extractApiError(e)) });

  if (!open) return null;
  const r: Rfq | undefined = rfq.data;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{r?.code} — {r?.title} <Chip size="small" label={r?.status} sx={{ ml: 1 }} /></DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {toast && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setToast(null)}>{toast}</Alert>}

        {r?.status === 'sent' && hasPermission('quotation.create') && (
          <Box mb={2}>
            <Typography variant="subtitle2">Record vendor quotation</Typography>
            <Stack direction="row" spacing={1} alignItems="center" mt={1} mb={1}>
              <TextField select size="small" label="Vendor" value={quoteVendor} onChange={(e) => setQuoteVendor(Number(e.target.value))} sx={{ minWidth: 160 }}>
                {(r.vendorIds ?? []).map((v) => <MenuItem key={v} value={v}>Vendor #{v}</MenuItem>)}
              </TextField>
              <Button variant="contained" size="small" disabled={!quoteVendor} onClick={() => submitQuote.mutate()}>Submit Quote</Button>
            </Stack>
            <Table size="small">
              <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Qty</TableCell><TableCell>Unit Rate</TableCell></TableRow></TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.description}</TableCell><TableCell>{it.quantity}</TableCell>
                    <TableCell><TextField variant="standard" type="number" value={rates[it.id] ?? ''} onChange={(e) => setRates((s) => ({ ...s, [it.id]: Number(e.target.value) }))} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" gutterBottom>Commercial comparison</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Rank</TableCell><TableCell>Quote</TableCell><TableCell>Vendor</TableCell><TableCell>Grand Total</TableCell><TableCell>Saving</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(comparison.data?.quotations ?? []).map((q) => (
              <TableRow key={q.quotationId} selected={r?.selectedQuotationId === q.quotationId}>
                <TableCell>{q.rank}</TableCell><TableCell>{q.code}</TableCell><TableCell>#{q.vendorId}</TableCell>
                <TableCell>₹{q.grandTotal.toLocaleString()}</TableCell><TableCell>₹{q.savingVsHighest.toLocaleString()}</TableCell>
                <TableCell>
                  {r?.status === 'sent' && hasPermission('rfq.update') && <Button size="small" onClick={() => selectMut.mutate(q.quotationId)}>Select</Button>}
                  {q.status === 'selected' && <Chip size="small" color="success" label="selected" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" spacing={1} mt={2}>
          {r?.selectionStatus === 'pending_approval' && hasPermission('po.approve') && <Button variant="outlined" color="success" onClick={() => approveMut.mutate()}>Approve Selection</Button>}
          {r?.status === 'awarded' && hasPermission('po.create') && <Button variant="contained" onClick={() => genPo.mutate()}>Generate PO</Button>}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
